import { makeStyles, Spinner } from "@fluentui/react-components";
import React from "react";
import { useApiResource } from "../../../../hooks/useApiResource";
import { IApiResourceResponse } from "../../../../models/resource-allocation/IResourceAvailabilityCard";
import {
  AIRPORT_CODE_PARAMETER,
  EMPTY_STATE_MESSAGE,
} from "../../../../utils/common/Constants";
import { transformApiResponse } from "../../../../utils/resource/ResourceAvailabilityHelper";
import { NoDataCard } from "../../../common/no-data-card/NoDataCard";
import ResourceAvailabilityFooter from "./ResourceAvailabilityFooter";
import ResourceAvailabilityHeader from "./ResourceAvailabilityHeader";
import ResourceRow from "./ResourceRow";

const resourcesEndpoint = process.env.REACT_APP_API_RESOURCEAVAILABILITY;

const payload = {
  ResourcesAvailabilityFilters: [
    {
      type: "stands",
      includeGeographicData: false,
      includeFlightData: false,
      excludeClassifications: ["REMOTE"],
      excludeOccupancies: ["UNKNOWN"],
      excludeStatuses: null,
    },
    {
      type: "gates",
      includeGeographicData: false,
      includeFlightData: false,
    },
    {
      type: "baggages",
      includeGeographicData: true,
      includeFlightData: false,
    },
  ],
};

export interface IResourceAvailabilityProps {
  airport: string;
}

const ResourceAvailability: React.FC<IResourceAvailabilityProps> = ({
  airport,
}) => {
  const classes = useStyles();

  const resourceUrl =
    resourcesEndpoint?.replace(AIRPORT_CODE_PARAMETER, airport) || "";

  const {
    data: resourceData,
    isLoading,
    isError,
  } = useApiResource({
    queryKey: ["resource-availability-card", airport],
    url: resourceUrl,
    fetchOnPost: true,
    fetchPayload: payload,
    refetchInterval: 60000,
  });

  const resources = resourceData
    ? transformApiResponse(resourceData as IApiResourceResponse).sort((a, b) =>
        a.title.localeCompare(b.title, undefined, {
          sensitivity: "base",
          numeric: true,
        })
      )
    : null;

  const hasNoData = resources !== null && resources.length === 0;

  const noDataCard = (
    <NoDataCard message={EMPTY_STATE_MESSAGE.Resource} marginAll />
  );

  return (
    <section
      className={classes.cardContainer}
      data-testid="resourceAvailabilityCard"
    >
      {(isError || hasNoData) && noDataCard}
      {isLoading && (
        <Spinner
          labelPosition="below"
          label="Loading..."
          style={{ margin: "auto" }}
        />
      )}
      {!isError && !isLoading && resources && resources.length > 0 && (
        <>
          <ResourceAvailabilityHeader />
          <section className={classes.cardBody}>
            {resources.map((resource) => (
              <ResourceRow key={resource.title} resource={resource} />
            ))}
          </section>
          <ResourceAvailabilityFooter />
        </>
      )}
    </section>
  );
};

export default ResourceAvailability;

const useStyles = makeStyles({
  cardContainer: {
    padding: "8px",
    boxSizing: "border-box",
    gridRow: "span 2",
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },
  cardBody: {
    marginTop: "8px",
    flexGrow: 1,
  },
});
